import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { ApplicationState } from "@idw-types/state/application-state";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { MessageTemplatesTableContainer } from "./message-templates-table-container/message-templates-table-container";
import { getActiveReferenceData } from "@utils/data-util";
import { AddMessageTemplateModal } from "@containers/modals/add-message-template-modal/add-message-template-modal";
import { MESSAGE_TEMPLATES_REFERENCE_DATA_TYPES, MessageTemplatesReferenceData } from "@constants/reference-data";
import { getMessageTemplatesReduxRequest } from "@redux-requests/message-templates";

export const MessageTemplatesContainer = () => {
    const modalContext = useModalContext()

    const dispatch = useDispatch();
    const {
        isLoadingMessageTemplates,
        messageTemplates,
        messageTemplatesTableState,
        session
    } = useSelector((state: ApplicationState) => ({
        isLoadingMessageTemplates: state.messageTemplates.messageTemplates.isLoading,
        messageTemplates: state.messageTemplates.messageTemplates.messageTemplates,
        messageTemplatesTableState: state.tableSettings.messageTemplates,
        session: state.session.session
    }));

    const referenceData = useReferenceDataSelector<MessageTemplatesReferenceData>(MESSAGE_TEMPLATES_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadMessageTemplates, setShouldLoadMessageTemplates] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.messageTemplates && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                MESSAGE_TEMPLATES_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.MESSAGE_TEMPLATES
            );
        }
    }, [referenceData.isLoading.messageTemplates, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies
        );
    }, [
        referenceData.axnCompanies]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingMessageTemplates && shouldLoadMessageTemplates) {
            setShouldLoadMessageTemplates(false);
            getMessageTemplatesReduxRequest(dispatch)(referenceData.axnCompanies);
        }
    }, [isReferenceDataPresent, isLoadingMessageTemplates, shouldLoadMessageTemplates]);

    const {
        axnCompanies: activeAxnCompanies } = useMemo(() => {
            if (isReferenceDataPresent) {
                const { axnCompanies } = referenceData;

                return getActiveReferenceData({
                    axnCompanies
                });
            }

            return {
                axnCompanies: []
            };
        }, [isReferenceDataPresent, referenceData.axnCompanies]);

    const renderAddMessageTemplate = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddMessageTemplateModal
                session={session}
                messageTemplates={messageTemplates}
                serviceOrganizationId={null}
                onModalClose={onModalClose}
                axnCompanies={activeAxnCompanies}
                selfServiceVerificationWhiteLabeledEmails= {referenceData.selfServiceVerificationWhiteLabeledEmails}
                addToReferenceData={false}
            />
        );
    }, [activeAxnCompanies, messageTemplates]);

    const isLoading = useMemo(() => {
        return (
            isLoadingMessageTemplates ||
            referenceData.isLoading.messageTemplates
        );
    }, [
        isLoadingMessageTemplates,
        referenceData.isLoading.messageTemplates
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            messageTemplates &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData        );
    }, [isLoading, messageTemplates, isReferenceDataPresent, shouldLoadReferenceData, shouldLoadMessageTemplates]);


    const headerButton = useMemo(() => {
        if (session.userPermissions.canCreateSelfServiceVerificationMessageTemplates) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddMessageTemplate,
                    renderFunction: renderAddMessageTemplate
                });
            };
            return (
                <ButtonWithIcon
                    id="add-message-template-button"
                    title={resources.pages.messageTemplates.headerButton}
                    text={resources.pages.messageTemplates.headerButton}
                    onClick={onClick}
                    icon={faPlusCircle}
                    disabled={isLoading || !isLoaded}
                />
            );
        }

        return null;
    }, [isLoading, isLoaded, session, renderAddMessageTemplate]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader title={resources.pages.messageTemplates.header} headerButton={headerButton} />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <MessageTemplatesTableContainer
                        messageTemplates={messageTemplates}
                        tableState={messageTemplatesTableState}
                        isPageHeight={true}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};